import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Shield, ChevronDown, ChevronUp, Loader2, RefreshCw, AlertTriangle, CheckCircle, XCircle, Info } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Framework = 'eu_ai_act' | 'nist_ai_rmf' | 'iso_42001';
type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
type Severity = 'critical' | 'high' | 'medium' | 'low';

interface Finding {
  title: string;
  description: string;
  recommendation: string;
  severity: Severity;
}

interface FrameworkResult {
  framework: Framework;
  score: number;
  findings: Finding[];
}

interface ScanResult {
  id: string;
  created_at: string;
  system_description: string;
  risk_level: RiskLevel;
  overall_score: number;
  frameworks: FrameworkResult[];
  status: 'completed' | 'failed' | 'in_progress';
}

interface ScanHistoryResponse {
  scans: ScanResult[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FRAMEWORK_OPTIONS: { value: Framework; label: string }[] = [
  { value: 'eu_ai_act', label: 'EU AI Act' },
  { value: 'nist_ai_rmf', label: 'NIST AI RMF' },
  { value: 'iso_42001', label: 'ISO 42001' },
];

const RISK_LEVELS: { value: RiskLevel; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const FRAMEWORK_LABELS: Record<Framework, string> = {
  eu_ai_act: 'EU AI Act',
  nist_ai_rmf: 'NIST AI RMF',
  iso_42001: 'ISO 42001',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function scoreColor(score: number): string {
  if (score > 80) return 'text-green-500';
  if (score >= 50) return 'text-yellow-500';
  return 'text-red-500';
}

function scoreBgColor(score: number): string {
  if (score > 80) return 'bg-green-500/10 border-green-500/30';
  if (score >= 50) return 'bg-yellow-500/10 border-yellow-500/30';
  return 'bg-red-500/10 border-red-500/30';
}

function severityBadgeClasses(severity: Severity): string {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium inline-block';
  switch (severity) {
    case 'critical':
      return `${base} bg-red-500/15 text-red-500`;
    case 'high':
      return `${base} bg-orange-500/15 text-orange-500`;
    case 'medium':
      return `${base} bg-yellow-500/15 text-yellow-500`;
    case 'low':
      return `${base} bg-blue-500/15 text-blue-500`;
  }
}

function severityIcon(severity: Severity) {
  switch (severity) {
    case 'critical':
      return <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />;
    case 'high':
      return <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0" />;
    case 'medium':
      return <Info className="h-4 w-4 text-yellow-500 flex-shrink-0" />;
    case 'low':
      return <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ScanForm({
  onCancel,
  onScanComplete,
}: {
  onCancel: () => void;
  onScanComplete: (result: ScanResult) => void;
}) {
  const queryClient = useQueryClient();
  const [selectedFrameworks, setSelectedFrameworks] = useState<Set<Framework>>(
    new Set(['eu_ai_act', 'nist_ai_rmf', 'iso_42001']),
  );
  const [systemDescription, setSystemDescription] = useState('');
  const [riskLevel, setRiskLevel] = useState<RiskLevel>('medium');

  const scanSingleMutation = useMutation({
    mutationFn: (payload: { framework: Framework; system_description: string; risk_level: RiskLevel }) =>
      api<ScanResult>('governance/scan', { body: payload }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'history'] });
      onScanComplete(data);
    },
  });

  const scanAllMutation = useMutation({
    mutationFn: (payload: { system_description: string; risk_level: RiskLevel }) =>
      api<ScanResult>('governance/scan-all', { body: payload }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'history'] });
      onScanComplete(data);
    },
  });

  const isRunning = scanSingleMutation.isPending || scanAllMutation.isPending;
  const error = scanSingleMutation.error || scanAllMutation.error;

  function toggleFramework(fw: Framework) {
    setSelectedFrameworks((prev) => {
      const next = new Set(prev);
      if (next.has(fw)) {
        next.delete(fw);
      } else {
        next.add(fw);
      }
      return next;
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!systemDescription.trim() || selectedFrameworks.size === 0) return;

    const allSelected = selectedFrameworks.size === FRAMEWORK_OPTIONS.length;

    if (allSelected) {
      scanAllMutation.mutate({ system_description: systemDescription, risk_level: riskLevel });
    } else {
      // Scan the first selected framework (single scan endpoint)
      const framework = [...selectedFrameworks][0] as Framework;
      scanSingleMutation.mutate({ framework, system_description: systemDescription, risk_level: riskLevel });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-6 space-y-5">
      <h3 className="text-lg font-semibold text-foreground">Configure Scan</h3>

      {/* Framework selector */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Frameworks</label>
        <div className="flex flex-wrap gap-3">
          {FRAMEWORK_OPTIONS.map((fw) => (
            <label
              key={fw.value}
              className="flex items-center gap-2 cursor-pointer select-none text-sm text-foreground"
            >
              <input
                type="checkbox"
                checked={selectedFrameworks.has(fw.value)}
                onChange={() => toggleFramework(fw.value)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              {fw.label}
            </label>
          ))}
        </div>
      </div>

      {/* System description */}
      <div>
        <label htmlFor="system-description" className="block text-sm font-medium text-foreground mb-2">
          System Description
        </label>
        <textarea
          id="system-description"
          required
          rows={4}
          placeholder="Describe the AI system being scanned, including its purpose, data sources, and deployment context..."
          value={systemDescription}
          onChange={(e) => setSystemDescription(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
        />
      </div>

      {/* Risk level */}
      <div>
        <label htmlFor="risk-level" className="block text-sm font-medium text-foreground mb-2">
          Risk Level
        </label>
        <select
          id="risk-level"
          value={riskLevel}
          onChange={(e) => setRiskLevel(e.target.value as RiskLevel)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          {RISK_LEVELS.map((rl) => (
            <option key={rl.value} value={rl.value}>
              {rl.label}
            </option>
          ))}
        </select>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500">
          {error instanceof Error ? error.message : 'An unexpected error occurred.'}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={isRunning || !systemDescription.trim() || selectedFrameworks.size === 0}
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isRunning && <Loader2 className="h-4 w-4 animate-spin" />}
          {isRunning ? 'Scanning...' : 'Run Scan'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isRunning}
          className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function ScoreDisplay({ score, label }: { score: number; label?: string }) {
  return (
    <div className={`rounded-lg border p-6 text-center ${scoreBgColor(score)}`}>
      {label && <p className="text-sm font-medium text-muted-foreground mb-1">{label}</p>}
      <p className={`text-4xl font-bold ${scoreColor(score)}`}>{score}</p>
      <p className="text-xs text-muted-foreground mt-1">/ 100</p>
    </div>
  );
}

function FindingCard({ finding }: { finding: Finding }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start gap-3">
        {severityIcon(finding.severity)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h4 className="text-sm font-medium text-foreground">{finding.title}</h4>
            <span className={severityBadgeClasses(finding.severity)}>{finding.severity}</span>
          </div>
          <p className="text-sm text-muted-foreground mb-2">{finding.description}</p>
          <div className="rounded-md bg-accent/50 p-3">
            <p className="text-xs font-medium text-foreground mb-1">Recommendation</p>
            <p className="text-xs text-muted-foreground">{finding.recommendation}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScanResults({ result }: { result: ScanResult }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Scan Results</h3>
        <span className="text-sm text-muted-foreground">{formatDate(result.created_at)}</span>
      </div>

      {/* Overall score */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ScoreDisplay score={result.overall_score} label="Overall Compliance" />
        {result.frameworks.map((fw) => (
          <ScoreDisplay key={fw.framework} score={fw.score} label={FRAMEWORK_LABELS[fw.framework]} />
        ))}
      </div>

      {/* Findings by framework */}
      {result.frameworks.map((fw) => (
        <div key={fw.framework}>
          <h4 className="text-sm font-semibold text-foreground mb-3">
            {FRAMEWORK_LABELS[fw.framework]} -- {fw.findings.length} finding{fw.findings.length !== 1 ? 's' : ''}
          </h4>
          {fw.findings.length === 0 ? (
            <p className="text-sm text-muted-foreground">No findings for this framework.</p>
          ) : (
            <div className="space-y-3">
              {fw.findings.map((finding, i) => (
                <FindingCard key={`${fw.framework}-${i}`} finding={finding} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function HistoryRow({ scan }: { scan: ScanResult }) {
  const [expanded, setExpanded] = useState(false);

  const frameworkLabels = scan.frameworks
    .map((fw) => FRAMEWORK_LABELS[fw.framework])
    .join(', ');

  const statusBadge = (() => {
    const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
    switch (scan.status) {
      case 'completed':
        return <span className={`${base} bg-green-500/15 text-green-500`}>Completed</span>;
      case 'in_progress':
        return <span className={`${base} bg-blue-500/15 text-blue-500`}>In Progress</span>;
      case 'failed':
        return <span className={`${base} bg-red-500/15 text-red-500`}>Failed</span>;
    }
  })();

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-accent/50 transition-colors"
      >
        <span className="text-sm text-muted-foreground w-40 flex-shrink-0">{formatDate(scan.created_at)}</span>
        <span className="text-sm text-foreground flex-1 truncate">{frameworkLabels}</span>
        <span className={`text-sm font-semibold w-12 text-right ${scoreColor(scan.overall_score)}`}>
          {scan.overall_score}
        </span>
        <span className="w-24 flex justify-center">{statusBadge}</span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          <ScanResults result={scan} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function GovernanceScanner() {
  const [showForm, setShowForm] = useState(false);
  const [latestResult, setLatestResult] = useState<ScanResult | null>(null);

  const historyQuery = useQuery<ScanHistoryResponse>({
    queryKey: ['governance', 'history'],
    queryFn: () => api<ScanHistoryResponse>('governance/history'),
  });

  function handleScanComplete(result: ScanResult) {
    setLatestResult(result);
    setShowForm(false);
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Governance Scanner</h1>
            <p className="text-muted-foreground mt-1">
              Scan your AI systems against EU AI Act, NIST AI RMF, ISO 42001, and more.
              Get actionable compliance findings and recommendations.
            </p>
          </div>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => {
              setShowForm(true);
              setLatestResult(null);
            }}
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 flex-shrink-0"
          >
            <RefreshCw className="h-4 w-4" />
            Run New Scan
          </button>
        )}
      </div>

      {/* Scan form */}
      {showForm && (
        <ScanForm onCancel={() => setShowForm(false)} onScanComplete={handleScanComplete} />
      )}

      {/* Latest scan results */}
      {latestResult && (
        <div className="rounded-lg border border-border bg-card p-6">
          <ScanResults result={latestResult} />
        </div>
      )}

      {/* Scan history */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">Scan History</h2>

        {historyQuery.isLoading && (
          <div className="rounded-lg border border-border bg-card p-12 flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading scan history...</span>
          </div>
        )}

        {historyQuery.isError && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-6 text-center">
            <p className="text-sm text-red-500">
              Failed to load scan history.{' '}
              <button
                type="button"
                onClick={() => historyQuery.refetch()}
                className="underline hover:no-underline"
              >
                Retry
              </button>
            </p>
          </div>
        )}

        {historyQuery.isSuccess && historyQuery.data.scans.length === 0 && (
          <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
            No scans yet. Click "Run New Scan" to get started.
          </div>
        )}

        {historyQuery.isSuccess && historyQuery.data.scans.length > 0 && (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            {/* Table header */}
            <div className="flex items-center gap-4 px-4 py-2 bg-muted/50 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wider">
              <span className="w-40 flex-shrink-0">Date</span>
              <span className="flex-1">Frameworks</span>
              <span className="w-12 text-right">Score</span>
              <span className="w-24 text-center">Status</span>
              <span className="w-4" />
            </div>
            {/* Rows */}
            {historyQuery.data.scans.map((scan) => (
              <HistoryRow key={scan.id} scan={scan} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
